import { StatelessComponent, ReactNode } from 'react'
import * as styles from 'components/features/pfm/CreditTotalsOverview/CreditTotalsOverview.css'
import { MISSING_TIMEFRAME_DATA_MESSAGE } from 'constants/errors'
import { CreditTotals } from 'interfaces/credit'
import { TimeframeTyped } from 'interfaces/global'
import { formatPercent } from 'helpers/formatHelpers'
import { TooltipIcon } from 'components/features/pfm/Tooltip/TooltipIcon'
import {
  CREDIT_ACCOUNTS_HINT,
  CREDIT_AGE_HINT,
  CREDIT_AVAILABLE_HINT,
  CREDIT_BALANCE_HINT,
  CREDIT_INQUIRIES_HINT,
  CREDIT_UTILIZATION_HINT,
} from 'constants/hints'
import { formatTableCurrency } from 'helpers/tableHelpers'
import Overview from 'components/atoms/Overview'

export type CreditTotalsOverviewStateProps = {
  totals: CreditTotals | null
}
export type CreditTotalsOverviewOwnProps = TimeframeTyped
type CreditTotalsOverviewProps = CreditTotalsOverviewStateProps & CreditTotalsOverviewOwnProps

type BlockDetails = {
  title: string
  subtitle?: string
  value: ReactNode
  hint: string
}

const CreditTotalsOverview: StatelessComponent<CreditTotalsOverviewProps> = (props) => {
  const { totals } = props
  if (!totals) {
    return (
      <div className={ styles.alert }>
        { MISSING_TIMEFRAME_DATA_MESSAGE }
      </div>
    )
  }

  const {
    credit_age,
    available_credit,
    inquiries_count,
    open_accounts,
    total_balance,
    utilization,
  } = totals

  const blocks: BlockDetails[] = [{
    title: `# Of Open Accounts`,
    value: `${ open_accounts }`,
    hint: CREDIT_ACCOUNTS_HINT,
  }, {
    title: `Total Balance`,
    value: formatTableCurrency(total_balance),
    hint: CREDIT_BALANCE_HINT,
  }, {
    title: `Number\u00a0of\u00a0Inquiries`,
    subtitle: `(last 2 years)`,
    value: `${ inquiries_count }`,
    hint: CREDIT_INQUIRIES_HINT,
  }, {
    title: `Available Credit`,
    value: formatTableCurrency(available_credit),
    hint: CREDIT_AVAILABLE_HINT,
  }, {
    title: `Age of Credit`,
    value: `${ credit_age }${ credit_age > 0 ? ' yrs' : '' }`,
    hint: CREDIT_AGE_HINT,
  }, {
    title: `Account Utilization`,
    value: `${ formatPercent(utilization, 'one') }`,
    hint: CREDIT_UTILIZATION_HINT,
  }]

  return (
    <Overview>
      {
        blocks.map(({ title, subtitle, value, hint }, idx) => (
          <Overview.Section
            key={ idx }
            title={
              <>
                <div className={ styles.title }>
                  <span>{ title }</span>
                  <span className={ styles.tooltipIconWrapper }>
                    <TooltipIcon icon='question' wide>{ hint }</TooltipIcon>
                  </span>
                </div>
                { Boolean(subtitle) && <div className={ styles.subtitle }>{ subtitle }</div> }
              </>
            }
          >
            { value }
          </Overview.Section>
        ))
      }
    </Overview>
  )
}

export default CreditTotalsOverview
